import {Component, Inject, ViewEncapsulation} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {IPortfolioResultResponse, IPortfolioResultChartConfig} from '@irb/irb-editors/editor-portfolio-result/editor-portfolio-result.model';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import {IrbAgGridExportService} from '@irb-common/components/irebal-ag-grid-ent-avatar/irb-ag-grid-export/irb-ag-grid-export.service';
import { AppService } from '@irb/app.service';
import { UtilityService } from '@irb/irb-common/services/utility.service';

export interface ExportPortfolioResults {
    rebalanceId: number;
    rebalanceName: string;
    rebalanceType: string;
    customCategoryName: string;
    selectedPortfolios: Partial<IPortfolioResultResponse>[];
    isDetail: boolean;
    onContinue?: Function;
}

export interface JasperSettings {
    showSummaryReport: boolean;
    showCashReport: boolean;
    showOrderReport: boolean;
    showAllOrder: boolean;
    showAllcnReport: boolean;
    showAllcnDollarPercent: boolean;
    allcnGroup: string;
    allcnGroupCCName: string;
    allcnSortModel: Array<ISortModel>;
    orderSortModel: Array<ISortModel>;
    cashSortModel: Array<ISortModel>;
    showAllcnAbsVar: boolean;
    showAllcnExcludedPositions: boolean;
    showAllcnGroupEquivalents: boolean;
    showAllcnChartReport: boolean;
    chartGroupCCName: string;
    assetClassDetail1: boolean;
    assetClassDetail2: boolean;
    assetClassDetail3: boolean;
    assetClassDetail4: boolean;
    allcnChartConfig: IPortfolioResultChartConfig;
}

export interface ISortModel {
    sort: string;
    order: string;
}

@Component({ standalone: false,
    selector: 'irb-dialog-pr-export-pdf',
    templateUrl: './dialog-pr-export-pdf.component.html',
    styleUrls: ['./dialog-pr-export-pdf.component.scss', '../../irb-common/styles/dialog.scss'],
    encapsulation: ViewEncapsulation.None
})
export class DialogPrExportPdfComponent {
    private isRebalanceService: boolean = false;
    config: JasperSettings;

    constructor(
        private dialogRef: MatDialogRef<DialogPrExportPdfComponent>,
        private _http: HttpClient,
        @Inject(MAT_DIALOG_DATA) public data: ExportPortfolioResults,
        private ges: IrbAgGridExportService, 
        private as: AppService,
        private util: UtilityService
    ) {        
        this.config = {
            showSummaryReport: true,
            showCashReport: true,
            showOrderReport: true,
            showAllOrder: false,
            showAllcnReport: true,
            showAllcnDollarPercent: false,
            allcnGroup: 'POSITION',
            allcnGroupCCName: this.data.customCategoryName,
            allcnSortModel: [{sort: "secSymb", order: "asc"}],
            orderSortModel: [{sort: "accountNum", order: "asc"}],
            cashSortModel: [{sort: "accountNum", order: "asc"}],
            showAllcnAbsVar: false,
            showAllcnExcludedPositions: true,
            showAllcnGroupEquivalents: true,
            showAllcnChartReport: true,
            chartGroupCCName: this.data.customCategoryName,
            assetClassDetail1: true,
            assetClassDetail2: true,
            assetClassDetail3: true,
            assetClassDetail4: true,
            allcnChartConfig: {
                showPositions: true,
                showEquivalents: true,
                showSAC: true,
                showSACLevel1: true,
                showSACLevel2: false,
                showSACLevel3: false,
                showSACLevel4: false,
                showCustCat: true,
                showMorningstarStockStyles: true,
                showSACSecurityType: true,
                chartStyle: "BAR_CHART",
                sortMap: {
                    "POSITION": {column: "label", order: "asc"},
                    "SEC_CATEGORY": {column: "label", order: "asc"},
                    "STYLE_BOX": {column: "label", order: "asc"},
                    "SEC_TYPE": {column: "label", order: "asc"},
                    "SAC1": {column: "label", order: "asc"},
                    "SAC2": {column: "label", order: "asc"},
                    "SAC3": {column: "label", order: "asc"},
                    "SAC4": {column: "label", order: "asc"}
                }
            }
        } as JasperSettings;

        let c =  window.localStorage.getItem("PRD_EXPORT_PDF_CONFIG");

        if(c && this.util.compareObjectKeys(this.config, JSON.parse(c), ['allcnSortModel','orderSortModel','cashSortModel'])) {
            this.config = JSON.parse(c);
        }

        this.isRebalanceService = this.as?.getItem('IRB_APP_CONFIG')?.featureFlags?.ENABLE_REBALANCE_APIS || false;
    }

    get isDetail(): boolean {
        return this.data && this.data.isDetail;
    }

    get isSummary(): boolean {
        return !this.isDetail;
    }

    get isFormValid(): boolean {
        return this.config.showSummaryReport 
            || this.config.showCashReport 
            || this.config.showOrderReport
            || this.config.showAllcnReport
            || this.config.showAllcnChartReport;
    }

    close() {
        this.dialogRef.close();
    }

    continue() {
        const payload = {
            isDetail: this.data.isDetail,
            jasperRequest: {
                rebalPkgId: this.data.rebalanceId,
                rebalName: this.data.rebalanceName,
                rebalType: this.data.rebalanceType,
                portfolioList: this.data.selectedPortfolios.map(portfolio => ({
                    portfolioId: portfolio.portfolioId,
                    portfolioName: portfolio.portfolioName
                })),
                jasperSettingDto: this.config
            }
        }
        
        this.getJasperReport(payload).subscribe((_res: any) => {
            this.ges.showSuccessMessage();
        });

        window.localStorage.setItem("PRD_EXPORT_PDF_CONFIG", JSON.stringify(this.config));

        if(this.data.onContinue) {
            this.data.onContinue();
        }

        this.dialogRef.close();
    }

    getJasperReport(data: any): Observable<Object> {
        const headers = new HttpHeaders({
            'Accept': 'application/pdf',
            'Content-Type': 'application/json'
        });
        const url = this.isRebalanceService ? "/rebalanceservice/portfolio/async/get-jasper-report" : "/irebalone/async/get-jasper-report";
        return this._http.post(url, JSON.stringify(data), { headers: headers });
    }
}
